<template>
  <model-form
    ref="modelForm"
    :model="user"
    @change="$emit('change')"
  >
    <template
      v-if="user.isDeleted"
      #errors
    >
      User has been deleted
    </template>
    <template
      v-if="user.type === USER_TYPES.MARKETING"
    >
      <div class="row q-col-gutter-md">
        <model-form-input
          field="firstName"
          class="col-4"
          :label="$t('form.labels.firstName')"
          :rules="[val => !!val || 'Field is required']"
          autofocus
        />
        <model-form-input
          field="lastName"
          class="col-4 sensitive-input"
          :label="$t('form.labels.lastName')"
          :rules="[val => !!val || 'Field is required']"
        />
        <model-form-input
          field="email"
          class="col-4 sensitive-input"
          :label="$t('form.labels.emailAddress')"
        />
      </div>
      <div class="row justify-center q-mt-lg q-mb-xl q-gutter-md">
        <div class="col-4">
          <q-select
            v-if="currentUser.admin"
            v-model="user.siteId"
            class="col-4"
            label="Site"
            :options="$_.map(currentCircuit.sites.data, ({ id, name }) => ({ label: name, value: id }))"
            emit-value
            map-options
            :disable="user.loading || hasDirtySubscriptions || !$isPermitted($permissions.Customer.Manage)"
          />
          <action-not-permitted-tooltip v-if="!$isPermitted($permissions.Customer.Manage)" />
          <q-tooltip v-else-if="hasDirtySubscriptions">
            Please save your changes to continue
          </q-tooltip>
        </div>
        <q-btn
          v-if="hasCustomerioAccess"
          class="col-4"
          label="View All Messages"
          outline
          color="primary"
          :href="user.customerioUserProfileUrl"
          target="__blank"
        />
      </div>
      <div class="row justify-center">
        <user-marketing-subscriptions
          class="col-auto"
          :user="user"
        />
      </div>
      <div class="row q-col-gutter-md q-mb-xl">
        <div v-if="user.pessimisticData.excludeFromUserList">
          <model-form-checkbox
            field="excludeFromUserList"
            class="col-4"
            label="Hidden From User Queries"
          />
          <q-tooltip>
            This user is excluded from default user queries, as such it will not appear in user searches.
            This is usually enabled if the user is a guest user and we have not got personal information for this person.
          </q-tooltip>
        </div>
      </div>
    </template>
    <template
      v-if="user.type === USER_TYPES.STANDARD"
    >
      <q-tabs
        v-model="tab"
        dense
        class="text-grey"
        active-color="primary"
        indicator-color="primary"
        align="justify"
      >
        <q-tab
          name="details"
          label="Details"
        />
        <q-tab
          name="orders"
          label="Orders"
        />
        <q-tab
          v-if="$isPermitted($permissions.Customer.ViewMemberships)"
          name="owned-memberships"
          label="Owned Memberships"
        />
        <q-tab
          v-if="$isPermitted($permissions.Promotions.View)"
          name="vouchers"
          label="Vouchers"
        />
        <q-tab
          v-if="$isPermitted($permissions.Marketing.View)"
          name="messages"
          label="Messages"
        />
        <q-tab
          v-if="canAccessSecurityTab"
          name="security"
          label="Employee"
        />
      </q-tabs>
      <q-tab-panels
        v-model="tab"
        animated
      >
        <q-tab-panel
          name="details"
        >
          <div>
            <div class="row q-col-gutter-md">
              <model-form-input
                field="firstName"
                class="col-6"
                :label="$t('form.labels.firstName')"
                :rules="[val => !!val || 'Field is required']"
                autofocus
              />
              <model-form-input
                field="lastName"
                class="col-6 sensitive-input"
                :label="$t('form.labels.lastName')"
                :rules="[val => !!val || 'Field is required']"
              />
            </div>
            <div class="row q-col-gutter-md">
              <div class="col-5">
                <model-form-input
                  field="email"
                  class="col sensitive-input"
                  :label="$t('form.labels.emailAddress')"
                />
              </div>
              <div
                class="col-1 column items-start justify-center q-py-none"
                :class="{ 'q-pl-none': !$q.screen.gt.xs, 'q-pl-md': $q.screen.gt.xs }"
              >
                <q-btn
                  v-if="currentUser.admin && user.originalData.email"
                  dense
                  icon="lock"
                  @click="$refs.resetPasswordDialog.show()"
                >
                  <q-tooltip>
                    Reset password and send password reset email.
                  </q-tooltip>
                </q-btn>

                <q-btn
                  v-if="currentUser.admin"
                  dense
                  icon="fa fa-sign-out"
                  :disable="!$isPermitted($permissions.Customer.Manage)"
                  @click="logOutOfAllSessions"
                >
                  <q-tooltip>
                    Log this user out of all sessions.
                    <span v-if="!$isPermitted($permissions.Customer.Manage)">
                      You do not have the required permissions to log this user out of all sessions.
                    </span>
                  </q-tooltip>
                </q-btn>
              </div>
              <phone-number-input
                :phone="user.phone"
                class="col-6 sensitive-input q-my-sm"
                :masked-by-api="$_.includes(user.maskedFields, 'phone')"
                @phone-changed="handlePhoneChange"
              />
            </div>
            <div class="row q-col-gutter-md">
              <div class="col-5 row items-start">
                <model-form-input
                  field="dateOfBirth"
                  value-type="IsoDate"
                  emit-string="nullify-blank"
                  :disable="user.dateOfBirthVerified && !$register && !$admin"
                  class="col"
                  color="grey"
                  :label="$t('form.labels.dateOfBirth')"
                />
              </div>
              <div
                v-if="user.dateOfBirth && ($register || $admin)"
                class="column col-1 items-start"
                :class="{ 'q-pl-none': !$q.screen.gt.xs, 'q-pl-md': $q.screen.gt.xs }"
              >
                <div>Verified</div>
                <model-form-checkbox
                  field="dateOfBirthVerified"
                  class="col-auto"
                />
                <q-tooltip>
                  Verifying a user's date of birth will make them eligible for memberships where age verification is required.
                </q-tooltip>
              </div>
              <q-select
                ref="userClassSelect"
                v-model="user.userClass"
                class="col-6"
                :label="$t('user.userClass')"
                :options="userClassesForSelect"
                :display-value="user.userClass && user.userClass.name || 'None'"
                emit-value
                map-options
                bottom-slots
                color="grey"
                :rules="[val => canSkipUserClassRequirement || (!!val && val.isRemote) || 'Field is required']"
              >
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    v-on="scope.itemEvents"
                  >
                    <q-item-section
                      :class="{ 'text-bold': scope.selected }"
                    >
                      <q-item-label>
                        {{ scope.opt.label }}
                      </q-item-label>
                      <q-item-label caption>
                        {{ scope.opt.description }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>
            <div
              v-if="currentCircuit.supportsTranslations"
              class="row q-col-gutter-md"
            >
              <user-preferred-language-select
                :user="user"
                class="col-5"
              >
                <q-tooltip
                  anchor="top middle"
                  self="bottom middle"
                >
                  Displayed content and messages sent to the user will use this language whenever translations are available.
                </q-tooltip>
              </user-preferred-language-select>
            </div>
            <div
              v-if="geoapifyEnabled && !$_.includes(user.maskedFields, 'fullAddress')"
              class="row q-col-gutter-md q-mb-sm"
            >
              <div class="col-6">
                <address-input
                  :user="user"
                />
              </div>
            </div>
            <div class="row q-col-gutter-md">
              <div
                class="col-5 row items-start"
                @click="$isPermitted($permissions.Customer.ManageBenefits) && setUserPoints()"
              >
                <model-form-input
                  field="points"
                  class="col cursor-pointer"
                  label="Points"
                />
              </div>
              <div
                class="col-1 column items-start justify-center q-py-none"
                :class="{ 'q-pl-none': !$q.screen.gt.xs, 'q-pl-md': $q.screen.gt.xs }"
              >
                <q-btn
                  v-if="$isPermitted($permissions.Customer.ManageBenefits)"
                  dense
                  icon="edit"
                  @click="setUserPoints()"
                >
                  <q-tooltip>
                    Adjust points
                  </q-tooltip>
                </q-btn>
              </div>
              <div
                class="col-5 row items-start"
                @click="$isPermitted($permissions.Customer.ManageBenefits) && showUserCreditAdjustment()"
              >
                <model-form-input
                  field="credit"
                  class="col cursor-pointer"
                  label="Credit"
                  :prefix="currentCircuit.localeCurrencySymbol"
                />
              </div>
              <div
                class="col-1 column items-start justify-center q-py-none"
                :class="{ 'q-pl-none': !$q.screen.gt.xs, 'q-pl-md': $q.screen.gt.xs }"
              >
                <q-btn
                  v-if="$isPermitted($permissions.Customer.ManageBenefits)"
                  dense
                  icon="edit"
                  @click="showUserCreditAdjustment()"
                >
                  <q-tooltip>
                    Adjust credit
                  </q-tooltip>
                </q-btn>
              </div>
            </div>
            <div
              v-if="currentSite.allowInvoiceOrders"
              class="row q-col-gutter-md q-mb-sm"
            >
              <model-form-checkbox
                field="isInvoiceable"
                label="Is Invoiceable"
                class="col-4"
              />
              <model-form-input
                v-if="user.isInvoiceable"
                field="companyName"
                class="col-4 sensitive-input"
                label="Company Name"
              />
              <q-select
                v-if="user.isInvoiceable"
                v-model="user.billingAddressDetails"
                label="Address Details"
                class="col-4 sensitive-input q-pb-sm"
                multiple
                use-input
                use-chips
                new-value-mode="add"
              >
                <q-tooltip>Type each address line followed by the Enter Key</q-tooltip>
              </q-select>
            </div>
            <div class="row q-col-gutter-md q-mb-xl">
              <div class="row q-col-gutter-md">
                <div class="row q-col-gutter-md">
                  <div
                    v-if="$isPermitted($permissions.Customer.ViewMemberships)"
                    class="col-4"
                  >
                    <q-card
                      class="cursor-pointer"
                      style="height: 200px;"
                      flat
                      bordered
                      @click="showMembershipDetails"
                    >
                      <q-card-section class="row items-start">
                        <div class="col-auto text-body1">
                          Membership
                        </div>
                        <q-space />
                        <q-btn
                          v-if="user.membership.state === 'active' && user.membershipType.hasTiers && user.membershipTier && currentUser.admin"
                          class="col-auto"
                          dense
                          size="sm"
                          icon="edit"
                          :disable="!$isPermitted($permissions.Customer.ManageMemberships)"
                          @click.stop="addUserVisit()"
                        />
                      </q-card-section>

                      <q-card-section
                        :style="user.membership.state === 'active' && user.membershipType.hasTiers ? 'padding-top: 0; padding-bottom: 0;' : ''"
                      >
                        <div class="column">
                          <div class="row q-gutter-x-sm">
                            <q-chip
                              v-if="user.membership.state === 'active'"
                              square
                              :color="user.membership.state === 'active' ? 'primary' : null"
                              :text-color="user.membership.state === 'active' ? 'white' : null"
                              :label="user.membershipType && user.membershipType.name"
                            />
                            <q-icon
                              v-if="user.membership.state === 'active'"
                              name="check"
                              size="lg"
                              color="positive"
                            />
                            <q-icon
                              v-else-if="user.membership.state === 'pending'"
                              name="warning"
                              size="lg"
                              color="warning"
                            />
                            <q-icon
                              v-else-if="user.membership.state === 'inactive'"
                              name="cancel"
                              size="lg"
                              color="negative"
                            />
                            <div class="q-my-auto">
                              <span v-if="user.membership.state === 'active'">
                                Active
                              </span>
                              <span v-else-if="user.membership.state === 'inactive'">
                                No active membership
                              </span>
                              <span v-else-if="user.membership.state === 'pending'">
                                Pending membership charge
                              </span>
                            </div>
                          </div>
                          <div
                            v-if="user.membership.state === 'active' && user.membershipType.hasTiers"
                            class="row q-gutter-x-sm"
                          >
                            <q-chip
                              square
                              :color="user.membership.state === 'active' ? 'primary' : null"
                              :text-color="user.membership.state === 'active' ? 'white' : null"
                              :label="user.membershipTier && user.membershipTier.name"
                            />
                            <q-icon
                              v-if="membershipTierRequirements.length"
                              class="q-my-auto"
                              name="info"
                              size="sm"
                            >
                              <q-tooltip>
                                {{ membershipTierRequirements }}
                              </q-tooltip>
                            </q-icon>
                          </div>
                        </div>
                      </q-card-section>

                      <q-card-actions class="row justify-center q-ma-sm">
                        <q-btn
                          outline
                          @click="$editModel(user.membership)"
                        >
                          Details
                        </q-btn>
                      </q-card-actions>
                    </q-card>
                  </div>
                  <div
                    v-if="$isPermitted($permissions.Customer.ViewPayments)"
                    class="col-4"
                  >
                    <q-card
                      style="height: 200px;"
                      flat
                      bordered
                    >
                      <q-card-section>
                        <div class="text-body1">
                          Saved Credit Card
                        </div>
                      </q-card-section>

                      <q-card-section class="row items-center">
                        <div class="col-auto">
                          <q-icon
                            v-if="user.defaultPaymentMethodId"
                            name="credit_card"
                            size="lg"
                          />
                          <q-icon
                            v-else
                            name="error"
                            size="lg"
                            color="negative"
                          />
                        </div>
                        <div class="col column justify-center">
                          <div class="text-uppercase q-pl-sm">
                            {{ user.defaultPaymentMethodId ? (user.defaultPaymentMethodBrand + ' &nbsp;&nbsp;&nbsp;...' + user.defaultPaymentMethodLast4 ) : 'None' }}
                          </div>
                          <div
                            v-if="!!user.defaultPaymentMethodFingerprint"
                            class="text-caption text-italic q-pl-sm"
                            :class="$admin ? 'cursor-pointer' : ''"
                            @click="$admin && $search.show(user.defaultPaymentMethodFingerprint)"
                          >
                            {{ user.defaultPaymentMethodFingerprint }}
                            <q-icon
                              v-if="$admin"
                              class="q-pl-xs"
                              name="search"
                            />
                            <q-tooltip>
                              Stripe Fingerprint
                            </q-tooltip>
                          </div>
                        </div>
                      </q-card-section>

                      <q-card-actions class="row justify-evenly q-ma-sm">
                        <q-btn
                          outline
                          color="positive"
                          :disable="!$isPermitted($permissions.Customer.ManagePayments)"
                          @click="$refs.setupIntentDialog.show(user)"
                        >
                          {{ user.defaultPaymentMethodId ? $t('actions.update') : $t('actions.add') }}
                        </q-btn>
                        <q-btn
                          v-if="user.defaultPaymentMethodId"
                          outline
                          color="negative"
                          :loading="removeDefaultPaymentMethodLoading"
                          :disable="!$isPermitted($permissions.Customer.ManagePayments)"
                          @click="removeDefaultPaymentMethod"
                        >
                          {{ $t('actions.remove') }}
                        </q-btn>
                      </q-card-actions>
                    </q-card>
                  </div>
                  <div
                    v-if="$isPermitted($permissions.Customer.ViewMemberships)"
                    class="col-4"
                  >
                    <q-card
                      style="height: 200px;"
                      flat
                      bordered
                    >
                      <q-card-section>
                        <div class="text-body1">
                          {{ $t('membership.membershipCard') }}
                        </div>
                      </q-card-section>

                      <q-card-section class="row items-center">
                        <div class="col-auto">
                          <q-icon
                            v-if="user.card"
                            name="check"
                            size="lg"
                            color="positive"
                          />
                          <q-icon
                            v-else
                            name="error"
                            size="lg"
                            color="negative"
                          />
                        </div>
                        <div class="col column justify-center">
                          <div class="q-pl-sm">
                            {{ user.card ? '' : 'No ' }} Card Assigned
                          </div>
                          <div
                            v-if="user.card"
                            class="text-caption text-italic q-pl-sm"
                          >
                            {{ user.card.humanUid }}
                          </div>
                        </div>
                      </q-card-section>

                      <q-card-actions class="row justify-center q-ma-sm">
                        <div v-if="!user.card">
                          <!--
                            Key Is required on button due to issue with button focus helper on electron.
                            This allows scanner input to work by rerendering the button on load.
                          -->
                          <q-btn
                            :key="assignCardLoading"
                            outline
                            color="primary"
                            :disable="(!$register && !$admin) || !$isPermitted($permissions.Customer.ManageMemberships)"
                            :loading="assignCardLoading"
                            @click="assignCard"
                          >
                            Assign Card
                          </q-btn>
                          <q-tooltip v-if="!$register">
                            Cards can only be assigned from the register or admin applications.
                          </q-tooltip>
                        </div>
                        <q-btn
                          v-else
                          outline
                          color="negative"
                          :disable="!$isPermitted($permissions.Customer.ManageMemberships)"
                          @click="disassociateCard"
                        >
                          Disassociate Card
                        </q-btn>
                      </q-card-actions>
                    </q-card>
                  </div>
                  <div class="col-4 row justify-center">
                    <q-img
                      :src="user.photoUrl"
                      class="col-12 rounded-borders cursor-pointer"
                      style="min-width: 200px;"
                      @click="$refs.userPhotoDialog.show()"
                    />
                    <q-btn
                      class="col-12 q-mt-sm"
                      outline
                      color="positive"
                      @click="$refs.userPhotoDialog.show()"
                    >
                      Change Photo
                    </q-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </q-tab-panel>
        <q-tab-panel
          name="orders"
        >
          <order-table
            :user-id="user.id"
            :submodel="true"
            @select-order="selectOrder"
            @request-authorization="callback => $emit('request-authorization', callback)"
          />
        </q-tab-panel>
        <q-tab-panel
          v-if="$isPermitted($permissions.Customer.ViewMemberships)"
          name="owned-memberships"
        >
          <membership-table
            :user-id="user.id"
            :submodel="true"
            @modify-membership="modifyMembership"
          />
        </q-tab-panel>
        <q-tab-panel
          v-if="$isPermitted($permissions.Promotions.View)"
          name="vouchers"
        >
          <voucher-table
            :user-id="user.id"
            :display-search="false"
          />
        </q-tab-panel>
        <q-tab-panel
          v-if="$isPermitted($permissions.Marketing.View)"
          name="messages"
        >
          <div>
            <div class="row justify-center q-mt-lg q-mb-xl q-gutter-md">
              <div class="col-4">
                <q-select
                  v-if="currentUser.admin"
                  v-model="user.siteId"
                  class="col-4"
                  label="Site"
                  :options="$_.map(currentCircuit.sites.data, ({ id, name }) => ({ label: name, value: id }))"
                  emit-value
                  map-options
                  :disable="user.loading || hasDirtySubscriptions || !$isPermitted($permissions.Customer.Manage)"
                />
                <action-not-permitted-tooltip v-if="!$isPermitted($permissions.Customer.Manage)" />
                <q-tooltip v-else-if="hasDirtySubscriptions">
                  Please save your changes to continue
                </q-tooltip>
              </div>
              <q-btn
                v-if="hasCustomerioAccess"
                class="col-4"
                label="View All Messages"
                outline
                color="primary"
                :href="user.customerioUserProfileUrl"
                target="__blank"
              />
            </div>
            <div class="row justify-center">
              <user-marketing-subscriptions
                class="col-auto"
                :user="user"
              />
            </div>
          </div>
        </q-tab-panel>
        <q-tab-panel
          v-if="canAccessSecurityTab"
          name="security"
        >
          <!-- Employee Details Section -->
          <div class="row q-mb-md">
            <employee-details-card
              class="col"
              :user="user"
            />
          </div>
          <!-- Admin/Employee status section -->
          <div class="row q-mb-md">
            <access-level-summary-card
              class="col"
              :user="user"
              @save="fetchUser()"
            />
          </div>

          <!-- PIN Code section -->
          <div
            v-if="user.employee && (currentUser.admin || user.id === currentUser.id)"
            class="row q-mb-md"
          >
            <div class="col">
              <q-card
                flat
                bordered
              >
                <q-card-section horizontal>
                  <q-card-section class="col q-pb-none">
                    <div
                      class="text-h6"
                      style="font-weight: normal;"
                    >
                      PIN Code
                    </div>
                  </q-card-section>
                  <q-card-section class="col-auto q-pb-none">
                    <q-btn
                      label="Set PIN"
                      outline
                      @click="showSetPin = true"
                    />
                  </q-card-section>
                </q-card-section>
                <q-card-section class="q-pt-sm">
                  <div class="row">
                    <div class="col col-md-9">
                      <div class="text-grey-10">
                        Some POS app features can be configured to allow or require authorization via a 4-digit PIN code.
                        A unique PIN code can be set for each employee or admin user.
                      </div>
                      <div class="text-subtitle2 q-my-sm">
                        {{ user.hasPin ? 'This user has a PIN code' : 'This user does not have a PIN code' }}
                      </div>
                      To change:
                      <ol class="q-my-sm">
                        <li>Click the "Set PIN" button and enter new PIN code</li>
                        <li>Click the form "Save" button</li>
                      </ol>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>

          <!-- User roles section -->
          <div
            v-if="user.canHaveRoles && $isPermitted($permissions.Security.View)"
            class="row"
          >
            <div class="col">
              <user-roles-table :user="user" />
            </div>
          </div>
          <div
            v-if="$isPermitted($permissions.Team.View)"
            class="row q-mt-md"
          >
            <div class="col">
              <user-employee-job-types-table :user="user" />
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
      <setup-intent-dialog
        ref="setupIntentDialog"
      />
      <user-photo-dialog
        ref="userPhotoDialog"
        :user="user"
      />
      <user-points-dialog
        ref="userPointsDialog"
        :user="user"
      />
      <user-credit-adjustment-dialog
        ref="userCreditAdjustmentDialog"
        :user="user"
        @save="fetchUser()"
      />
      <user-visits-dialog
        ref="userVisitsDialog"
        :user="user"
      />
      <assign-card-dialog
        ref="assignCardDialog"
        :user="user"
      />
      <reset-password-dialog
        ref="resetPasswordDialog"
        :user="user"
      />
      <q-dialog v-model="showSetPin">
        <q-card>
          <q-card-section class="column items-center">
            <h3 class="q-ma-md text-center">
              Set {{ currentCircuit.userPinLength }}-Digit PIN
            </h3>
          </q-card-section>

          <pin-pad
            :loading="settingPin"
            @pin-pad-input="pin => { user.pin = pin; showSetPin = false }"
          />
        </q-card>
      </q-dialog>
    </template>
  </model-form>
</template>

<script>
import { date } from 'quasar'
import { geoapifyEnabled } from 'src/config'
import Card from 'src/models/card/Card'
import { IsoDate } from 'src/models/model'
import { USER_TYPES } from 'src/models/user/User'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import UserPreferredLanguageSelect from 'src/modules/admin/components/UserPreferredLanguageSelect'
import MembershipTable from 'src/modules/admin/membership/MembershipTable'
import OrderTable from 'src/modules/admin/order/OrderTable'
import AssignCardDialog from 'src/modules/admin/user/AssignCardDialog'
import ResetPasswordDialog from 'src/modules/admin/user/PasswordResetDialog'
import SetupIntentDialog from 'src/modules/admin/user/SetupIntentDialog'
import UserCreditAdjustmentDialog from 'src/modules/admin/user/UserCreditAdjustmentDialog'
import UserEmployeeJobTypesTable from 'src/modules/admin/user/UserEmployeeJobTypesTable'
import AccessLevelSummaryCard from 'src/modules/admin/user/UserForm/components/AccessLevelSummaryCard'
import EmployeeDetailsCard from 'src/modules/admin/user/UserForm/components/EmployeeDetailsCard'
import UserMarketingSubscriptions from 'src/modules/admin/user/UserMarketingSubscriptions'
import UserPhotoDialog from 'src/modules/admin/user/UserPhotoDialog'
import UserPointsDialog from 'src/modules/admin/user/UserPointsDialog'
import UserRolesTable from 'src/modules/admin/user/UserRolesTable'
import UserVisitsDialog from 'src/modules/admin/user/UserVisitsDialog'
import VoucherTable from 'src/modules/admin/voucher/VoucherTable'
import AddressInput from 'src/modules/auth/components/AddressInput'
import PhoneNumberInput from 'src/modules/consumer/components/phone/PhoneNumberInput'
import PinPad from 'src/modules/register/components/PinPad'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'UserForm',
  components: {
    ActionNotPermittedTooltip: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/ActionNotPermittedTooltip')),
    ModelForm,
    OrderTable,
    MembershipTable,
    AssignCardDialog,
    SetupIntentDialog,
    UserPhotoDialog,
    UserPreferredLanguageSelect,
    UserPointsDialog,
    UserCreditAdjustmentDialog,
    UserRolesTable,
    UserVisitsDialog,
    VoucherTable,
    UserMarketingSubscriptions,
    ResetPasswordDialog,
    PinPad,
    AccessLevelSummaryCard,
    AddressInput,
    UserEmployeeJobTypesTable,
    EmployeeDetailsCard,
    PhoneNumberInput
  },
  emits: ['change', 'request-authorization', 'fix-pending-membership', 'select-order', 'modify-membership'],
  data: function () {
    return {
      tab: 'details',
      assignCardLoading: false,
      removeDefaultPaymentMethodLoading: false,
      showSetPin: false,
      settingPin: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite', 'userClasses'],
  models: {
    user: {
      customerioUserProfileUrl: true,
      credit: true,
      deletedAt: true,
      membership: {
        members: {
          fields: ['id', 'membershipType']
        },
        currentMembershipInterval: {
          amount: true
        }
      },
      membershipType: true,
      membershipTier: true,
      card: 'default',
      authorizedSites: {
        fields: ['id', 'name', 'circuitId', 'isLibrary']
      },
      authorizedCircuits: {
        fields: ['id', 'name']
      },
      hasPin: true,
      userMarketingSubscriptions: {
        marketingSubscription: true
      },
      userStripeCustomers: 'default',
      roles: {
        users: {
          fields: ['id']
        },
        rolePermissions: {
          fields: ['action']
        }
      },
      userEmployeeJobTypes: {
        employeeJobType: {
          fields: ['id', 'name', 'hourlyRate', 'displayOrder', 'siteId']
        }
      }
    }
  },
  computed: {
    canAccessSecurityTab () {
      return this.currentUser.admin || this.currentUser.superUser || this.currentUser.id === this.user.id
    },
    authorizedSitesChanged () {
      return !!_.xor(_.map(this.user.originalData.authorizedSites, 'id'), _.map(this.user.authorizedSites.data, 'id')).length
    },
    hasCustomerioAccess () {
      return (
        this.user.customerioUserProfileUrl &&
        (this.currentUser?.superUser || (this.currentUser?.admin && this.currentCircuit.customerioWorkspaceIsConfigured))
      )
    },
    hasDirtySubscriptions () {
      return this.user.isDirty && _.some(this.user.describeDirty.userMarketingSubscriptions, ums => !!ums?.id)
    },
    canSkipUserClassRequirement () {
      // Super users will often have user classes belonging to other circuits which means
      // none of the select options will be enabled and would block form submit.
      return this.user?.superUser && !!this.user?.userClass
    },
    userClassesForSelect () {
      const userClasses = this.userClasses.data
      return _.map(userClasses, userClass => {
        return {
          value: userClass,
          label: userClass.name,
          description: userClass.description || userClass.ageDescription,
          disable: false
        }
      })
    },
    membershipTierRequirements () {
      let membershipTierRequirements = ''

      if (this.user.membershipType.hasTiers) {
        membershipTierRequirements += `${this.user.membershipTierRequirements.visits_attained} visit${this.user.membershipTierRequirements.visits_attained === 1 ? '' : 's'}`
        membershipTierRequirements += `, ${this.user.membershipTierRequirements.tickets_attained} ticket${this.user.membershipTierRequirements.tickets_attained === 1 ? '' : 's'}`
        membershipTierRequirements += `, ${this.user.membershipTierRequirements.movies_attained} movie${this.user.membershipTierRequirements.movies_attained === 1 ? '' : 's'}`
        membershipTierRequirements += `, ${this.user.membershipTierRequirements.points_attained} point${this.user.membershipTierRequirements.points_attained === 1 ? '' : 's'}`
        membershipTierRequirements += `, ${this.user.membershipTierRequirements.points_balance_attained} point${this.user.membershipTierRequirements.points_balance_attained === 1 ? '' : 's'} balance`

        if (this.user.tierDaysRemaining) {
          membershipTierRequirements += `, ${this.user.tierDaysRemaining} day${this.user.tierDaysRemaining === 1 ? '' : 's'} left`
        }
      }

      return membershipTierRequirements
    }
  },
  watch: {
    'user.dateOfBirth': {
      handler () {
        this.validateUserClasses()
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { IsoDate, USER_TYPES, geoapifyEnabled })
  },
  methods: {
    show (model, initialData, tab) {
      this.tab = tab || 'details'
      this.$refs.modelForm.show(model, initialData)
    },
    handlePhoneChange (phoneObject) {
      if (phoneObject?.isValid?.()) {
        this.user.phone = phoneObject.nationalNumber
        this.user.phoneCountryCode = phoneObject.country
      } else {
        this.user.phone = null
        this.user.phoneCountryCode = null
      }
    },
    fetchUser () {
      this.user.fetch(true)
    },
    setUserPoints () {
      if (this.currentUser.admin) {
        this.$refs.userPointsDialog.show()
      } else {
        this.$emit('request-authorization', () => { this.$refs.userPointsDialog.show() })
      }
    },
    showUserCreditAdjustment () {
      if (this.currentUser.admin) {
        this.$refs.userCreditAdjustmentDialog.show()
      } else {
        this.$emit('request-authorization', () => { this.$refs.userCreditAdjustmentDialog.show() })
      }
    },
    addUserVisit () {
      if (this.currentUser.admin) {
        this.$refs.userVisitsDialog.show()
      } else {
        this.$emit('request-authorization', () => { this.$refs.userVisitsDialog.show() })
      }
    },
    assignCard () {
      if (this.$admin) {
        this.$refs.assignCardDialog.show()
      } else {
        this.assignCardLoading = true
        this.$cardHandler.requestBlankCardScan('user', [], (uid) => {
          this.assignCardLoading = false
          if (!uid) return

          this.user.card = new Card({ uid })
          this.user.save()
        })
      }
    },
    async disassociateCard () {
      this.user.loading = true
      await this.user.card.delete()
      this.user.fetch(true)
    },
    validateUserClasses () {
      if (!this.user.dateOfBirth || this.user.dateOfBirth.substr(0, 4) === '0000') {
        this.userClassesForSelect.forEach(userClassOption => { userClassOption.disable = false })
        return
      }

      // Make options available based on age
      const birthdate = date.extractDate(this.user.dateOfBirth, 'YYYY-MM-DD')
      const today = new Date()

      const age = Math.floor(date.getDateDiff(today, birthdate, 'days') / 365)

      if (age < 0 || age > 200) return

      let firstMatch = null
      this.userClassesForSelect.forEach(userClassOption => {
        const min = userClassOption.value.minAge || 0
        const max = userClassOption.value.maxAge || 200

        const match = (age >= min && age <= max) && (this.user.membershipType.free || this.user.membershipType.membershipUserTypeForUserClass(userClassOption.value))

        userClassOption.disable = !match

        if (!firstMatch && !userClassOption.disable) firstMatch = userClassOption.value
      })

      if (firstMatch) this.user.userClass = firstMatch
    },
    modifyMembership () {
      if (this.user.isDirty) {
        this.$q.dialog({ title: 'Save user changes first' })
      } else {
        this.$refs.modelForm.close()
        this.$emit('modify-membership', this.user.id)
      }
    },
    showMembershipDetails () {
      if (this.user.isDirty) {
        this.$q.dialog({ title: 'Save user changes first' })
      } else {
        this.$refs.modelForm.close()
        this.$editModel(this.user.membership)
      }
    },
    fixPendingMembership () {
      if (this.user.isDirty) {
        this.$q.dialog({ title: 'Save user changes first' })
      } else {
        this.$refs.modelForm.close()
        this.$emit('fix-pending-membership')
      }
    },
    selectOrder (order) {
      if (this.user.isDirty) {
        this.$q.dialog({ title: 'Save user changes first' })
      } else {
        this.$refs.modelForm.close()
        this.$emit('select-order', order)
      }
    },
    async removeDefaultPaymentMethod () {
      this.removeDefaultPaymentMethodLoading = true
      await this.user.removeDefaultPaymentMethod()
      this.removeDefaultPaymentMethodLoading = false
    },
    async logOutOfAllSessions () {
      this.user.loading = true
      const results = await this.user.logOutOfAllSessions(this.user.id)
      if (!_.isEmpty(results.errors)) this.$mh.showErrors(results.errors)
      this.user.loading = false
    }
  }
}

</script>
